import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Clock, Tag, Bell, BellOff } from 'lucide-react';
import { api } from '../services/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useTheme } from './ThemeToggle';

const getTimeRemaining = (endDate) => {
  const total = new Date(endDate) - new Date();
  if (total <= 0) return { total: 0, hours: 0, minutes: 0, seconds: 0 };
  
  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));
  
  return { total, days, hours, minutes, seconds };
};

export default function FlashOffers({ customerId }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const { isDarkMode } = useTheme();

  const loadOffers = async () => {
    try {
      const data = await api.getFlashOffers();
      setOffers(data.offers || []);
    } catch (error) {
      console.error('Erreur chargement offres:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOffers();
    const interval = setInterval(loadOffers, 30000);
    return () => clearInterval(interval);
  }, []);

  const toggleNotifications = async () => {
    if (!notificationsEnabled && 'Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        toast.success('Notifications activées !');
      }
    } else {
      setNotificationsEnabled(!notificationsEnabled);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <motion.div 
          className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center"
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Zap className="w-5 h-5 text-amber-400" />
        </motion.div>
      </div>
    );
  }

  const activeOffers = offers.filter(o => new Date(o.endDate) > new Date());

  // État vide stylisé
  if (activeOffers.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #451a03 100%)',
          boxShadow: '0 0 40px rgba(245, 158, 11, 0.15), 0 15px 30px rgba(0,0,0,0.4)',
          border: '1px solid rgba(245, 158, 11, 0.2)'
        }}
      >
        {/* Étoiles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-amber-400"
              style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.3 + 0.1 }}
              animate={{ opacity: [0.1, 0.4, 0.1] }}
              transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
            />
          ))}
        </div>

        {/* HUD Corners */}
        <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-amber-500/40" />
        <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-amber-500/40" />
        <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-amber-500/20" />
        <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-amber-500/20" />

        <div className="relative px-5 py-8 text-center">
          <motion.div
            className="w-14 h-14 mx-auto mb-3 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center"
            animate={{ scale: [1, 1.05, 1], boxShadow: ['0 0 10px rgba(245, 158, 11, 0.1)', '0 0 20px rgba(245, 158, 11, 0.3)', '0 0 10px rgba(245, 158, 11, 0.1)'] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Zap className="w-7 h-7 text-amber-400/50" />
          </motion.div>
          <h4 className="text-white font-bold mb-1">⚡ Bons Plans</h4>
          <p className="text-gray-400 text-sm">Pas d'offre flash en ce moment</p>
          <p className="text-xs text-gray-600 mt-1 font-mono">STANDBY MODE • Revenez bientôt !</p>
        </div>

        {/* Footer HUD */}
        <div className="px-4 py-2 border-t border-amber-500/10 flex items-center justify-between">
          <span className="text-[10px] font-mono text-gray-600">⚡ FLASH DEALS v2.0</span>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-500/30" />
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400/20" />
            <div className="w-1.5 h-1.5 rounded-full bg-amber-300/10" />
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #451a03 100%)',
        boxShadow: '0 0 40px rgba(245, 158, 11, 0.2), 0 15px 30px rgba(0,0,0,0.4)',
        border: '1px solid rgba(245, 158, 11, 0.3)'
      }}
    >
      {/* Étoiles animées */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-amber-400"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.3 + 0.1 }}
            animate={{ opacity: [0.1, 0.5, 0.1], scale: [1, 1.3, 1] }}
            transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
          />
        ))}
      </div>

      <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />
      
      {/* HUD Corners */}
      <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-amber-500/50" />
      <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-amber-500/50" />
      <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-amber-500/30" />
      <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-amber-500/30" />

      {/* Header */}
      <div className="relative p-4 flex items-center justify-between border-b border-amber-500/20">
        <div className="flex items-center gap-3">
          <motion.div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)',
              border: '1px solid rgba(245, 158, 11, 0.3)'
            }}
            animate={{ boxShadow: ['0 0 10px rgba(245, 158, 11, 0.2)', '0 0 20px rgba(245, 158, 11, 0.4)', '0 0 10px rgba(245, 158, 11, 0.2)'] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.span
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
              className="text-xl"
            >
              ⚡
            </motion.span>
          </motion.div>
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              Bons Plans
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
                {activeOffers.length} ACTIF{activeOffers.length > 1 ? 'S' : ''}
              </span>
            </h3>
            <p className="text-[11px] text-amber-500/70 font-mono tracking-wider">
              DEALS EN COURS • TEMPS LIMITÉ
            </p>
          </div>
        </div>
        
        <Button
          onClick={toggleNotifications}
          size="sm"
          variant="ghost"
          className={`${notificationsEnabled ? 'text-amber-400' : 'text-gray-500'} hover:bg-amber-500/10`}
        >
          {notificationsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
        </Button>
      </div>

      {/* Offres */}
      <div className="relative p-4 space-y-3">
        <AnimatePresence>
          {activeOffers.map((offer, idx) => (
            <FlashOfferCard key={offer.id} offer={offer} index={idx} />
          ))}
        </AnimatePresence>
      </div>

      {/* Footer HUD */}
      <div className="px-4 py-2 border-t border-amber-500/10 flex items-center justify-between">
        <span className="text-[10px] font-mono text-gray-600">⚡ FLASH DEALS v2.0</span>
        <div className="flex items-center gap-1">
          <motion.div className="w-1.5 h-1.5 rounded-full bg-amber-500" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity }} />
          <div className="w-1.5 h-1.5 rounded-full bg-amber-400/50" />
          <div className="w-1.5 h-1.5 rounded-full bg-amber-300/30" />
        </div>
      </div>
    </motion.div>
  );
}

function FlashOfferCard({ offer, index }) {
  const [timeLeft, setTimeLeft] = useState(getTimeRemaining(offer.endDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getTimeRemaining(offer.endDate));
    }, 1000);
    return () => clearInterval(timer);
  }, [offer.endDate]);

  const isUrgent = timeLeft.total < 3600000;

  if (timeLeft.total <= 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ delay: index * 0.1 }}
      className="relative overflow-hidden rounded-xl p-4"
      style={{
        background: isUrgent 
          ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(249, 115, 22, 0.2) 100%)'
          : 'rgba(0,0,0,0.3)',
        border: isUrgent ? '2px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(245, 158, 11, 0.2)',
        boxShadow: isUrgent ? '0 0 30px rgba(239, 68, 68, 0.3)' : 'none'
      }}
    >
      {isUrgent && (
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 0.5, repeat: Infinity }}
          className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full"
        >
          🔥 DERNIÈRE CHANCE
        </motion.div>
      )}

      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
          <span className="text-2xl">{offer.emoji || '🎁'}</span>
        </div>
        <div className="flex-1">
          <h4 className="font-bold text-white">{offer.title}</h4>
          <p className="text-sm text-gray-400">{offer.description}</p>
        </div>
      </div>

      {offer.originalPrice && offer.discountPrice && (
        <div className="flex items-center gap-3 mb-3">
          <Tag className="w-4 h-4 text-amber-400" />
          <span className="text-gray-500 line-through text-sm">{offer.originalPrice}€</span>
          <span className="text-2xl font-bold text-amber-400">{offer.discountPrice}€</span>
          <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
            -{Math.round((1 - offer.discountPrice / offer.originalPrice) * 100)}%
          </span>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Clock className={`w-4 h-4 ${isUrgent ? 'text-red-400' : 'text-amber-400'}`} />
        <span className={`text-sm ${isUrgent ? 'text-red-400' : 'text-gray-400'}`}>Expire dans :</span>
        <div className="flex gap-1">
          {timeLeft.days > 0 && (
            <span className="px-2 py-1 rounded text-sm font-mono bg-black/50 text-white">{timeLeft.days}j</span>
          )}
          <span className={`px-2 py-1 rounded text-sm font-mono font-bold ${
            isUrgent ? 'bg-red-500 text-white' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
          }`}>
            {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
          </span>
        </div>
      </div>
    </motion.div>
  );
}
